import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supportedLanguages, type SupportedLanguage } from '@/i18n';
import { findRouteKeyFromPath } from '@/i18n/routes';

function extractPathInfo(pathname: string) {
  const segments = pathname.split('/').filter(Boolean);
  const lang = segments[0] as SupportedLanguage;
  const pathSegment = segments.slice(1).join('/') || '';
  return { lang, pathSegment };
}

function getRouteKey(pathname: string, lang: SupportedLanguage): string | null {
  const { pathSegment } = extractPathInfo(pathname);
  // Se il path è vuoto, è la home
  if (!pathSegment) {
    return 'home';
  }
  return findRouteKeyFromPath(pathSegment, lang);
}

export function ScrollToTop() {
  const { pathname } = useLocation();
  const prevPathRef = useRef<string>('');
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Salta il primo render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      prevPathRef.current = pathname;
      return;
    }

    const prevPathname = prevPathRef.current;

    // Se il pathname non è cambiato, non fare nulla
    if (prevPathname === pathname) {
      return;
    }

    const prevInfo = extractPathInfo(prevPathname);
    const currentInfo = extractPathInfo(pathname);

    // Se manca la lingua in uno dei due, scrolla (caso strano)
    if (!supportedLanguages.includes(prevInfo.lang) || !supportedLanguages.includes(currentInfo.lang)) {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      prevPathRef.current = pathname;
      return;
    }

    // Trova le route keys
    const prevRouteKey = getRouteKey(prevPathname, prevInfo.lang);
    const currentRouteKey = getRouteKey(pathname, currentInfo.lang);

    // Se è la stessa route (stessa route key), non scrollare
    // Questo include il cambio lingua (es: /it/chi-siamo -> /en/about)
    if (prevRouteKey && currentRouteKey && prevRouteKey === currentRouteKey) {
      prevPathRef.current = pathname;
      return;
    }

    // Altrimenti scrolla in alto (navigazione normale tra pagine diverse)
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });

    prevPathRef.current = pathname;
  }, [pathname]);

  return null;
}
